import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../hooks/useApi";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, Clock, MailWarning, RotateCcw, Ban, ShoppingCart, Loader2,
  Plus, Trash2, Upload, Pencil, Eye, Image as ImageIcon, X, Search, Music, Film, FileText,
  ArrowLeft, Star, Archive, ShieldAlert, Check,
} from "lucide-react";

const CONTENT_TABS = [
  { key: "episode", label: "Episodes" },
  { key: "signal", label: "Signals" },
  { key: "video", label: "Videos" },
  { key: "framework", label: "Frameworks" },
  { key: "companion_asset", label: "Companion Assets" },
];

const DEFAULT_TITLE = { episode: "Untitled Episode", signal: "Untitled Signal", video: "Untitled Video",
  framework: "Untitled Framework", companion_asset: "Untitled Companion Asset" };

const STATUS_META = {
  checkout_initiated: { label: "Checkout Initiated", icon: ShoppingCart, style: "text-foreground/70 border-foreground/35" },
  payment_confirmed: { label: "Payment Confirmed", icon: Clock, style: "text-foreground/70 border-foreground/35" },
  enrollment_awaiting_activation: { label: "Awaiting Activation", icon: MailWarning, style: "text-foreground border-foreground border-dashed" },
  activation_expired: { label: "Activation Expired", icon: AlertTriangle, style: "text-foreground border-foreground border-dashed" },
  enrollment_activated: { label: "Activated", icon: CheckCircle2, style: "text-foreground border-foreground" },
  refunded_or_disputed: { label: "Refunded / Disputed", icon: Ban, style: "text-foreground/70 border-foreground/35" },
  fulfillment_intervention_required: { label: "Needs Intervention", icon: AlertTriangle, style: "text-foreground border-foreground" },
};
const RESENDABLE = new Set(["payment_confirmed", "enrollment_awaiting_activation", "activation_expired", "fulfillment_intervention_required"]);

const MEDIA_STATUS_META = {
  awaiting_media: { label: "Awaiting Media", style: "text-foreground/50 border-foreground/30" },
  media_uploaded: { label: "Media Uploaded", style: "text-foreground/85 border-foreground/50" },
  ready_for_review: { label: "Ready for Review", style: "text-brandAccent border-brandAccent border-dashed" },
  published: { label: "Published", style: "text-brandAccent border-brandAccent" },
  archived: { label: "Archived", style: "text-foreground/35 border-foreground/20" },
  needs_attention: { label: "Needs Attention", style: "text-red-400 border-red-400" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.payment_confirmed;
  const Icon = meta.icon;
  return (
    <span data-testid={`gold-claim-status-${status}`} className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border px-3 py-1 ${meta.style}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function MediaStatusBadge({ status }) {
  const meta = MEDIA_STATUS_META[status] || MEDIA_STATUS_META.awaiting_media;
  return (
    <span data-testid={`media-status-badge-${status}`} className={`inline-flex items-center text-[11px] uppercase tracking-wide border px-2 py-0.5 ${meta.style}`}>
      {meta.label}
    </span>
  );
}

function ClaimRow({ claim, onResend, resendingId }) {
  const busy = resendingId === claim.session_id;
  return (
    <div className="border border-border p-4 mb-3 flex flex-wrap items-center gap-4 justify-between" data-testid={`gold-claim-row-${claim.session_id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{claim.email || "Email not yet provided"}</p>
        <p className="text-xs text-foreground/60 font-mono truncate">{claim.session_id} · {claim.offer_key}</p>
        <p className="text-xs text-foreground/70 mt-1">{claim.latest_event}{claim.created_at ? ` · opened ${new Date(claim.created_at).toLocaleString()}` : ""}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {claim.amount_cents != null && <span className="text-sm text-foreground/80">${(claim.amount_cents / 100).toFixed(2)}</span>}
        <StatusBadge status={claim.lifecycle_status} />
        {RESENDABLE.has(claim.lifecycle_status) && (
          <button data-testid={`gold-claim-resend-btn-${claim.session_id}`} onClick={() => onResend(claim.session_id)} disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors disabled:opacity-40">
            <RotateCcw className="w-3 h-3" /> {busy ? "Sending…" : "Resend Link"}
          </button>
        )}
      </div>
    </div>
  );
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Direct, cookie-authenticated media URL — browsers send real HTTP Range
 * requests for <audio>/<video> elements against these (no blob pre-fetch),
 * so scrubbing/seeking streams instead of downloading the whole file first.
 * The cache-busting `v` param forces a fresh fetch after a file replace,
 * since the URL path itself doesn't change when only the content changes. */
function adminPreviewUrl(id, kind, updatedAt) {
  const path = kind === "thumbnail" ? "preview-thumbnail" : "preview-file";
  return `${API_BASE}/admin/gold/content/${id}/${path}?v=${encodeURIComponent(updatedAt || "")}`;
}

/** A single upload slot (audio / video / document / artwork) with a real
 * upload-progress bar, inline preview once uploaded, and a "Replace File"
 * affordance — always visible inside the creation/edit form itself, never
 * gated behind a separate save step or list view. */
function MediaUploadField({ testid, label, icon: Icon, accept, hasFile, mediaKey, originalFilename, previewUrl, uploadFn, kind }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      await uploadFn(file, (pct) => setProgress(pct));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="border border-white/10 p-3" data-testid={`${testid}-field`}>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <span className="text-xs uppercase tracking-wide text-white/60 flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" /> {label}
        </span>
        <label className="inline-flex items-center gap-1.5 text-xs uppercase border border-border px-3 py-1.5 cursor-pointer hover:border-foreground transition-colors">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? `Uploading… ${progress}%` : hasFile ? "Replace File" : "Upload File"}
          <input type="file" accept={accept} className="hidden" data-testid={`${testid}-input`} disabled={uploading} onChange={handleChange} />
        </label>
      </div>
      {uploading && (
        <div className="w-full h-1.5 bg-white/10 overflow-hidden mb-2" data-testid={`${testid}-progress-bar`}>
          <div className="h-full bg-brandAccent transition-all duration-150" style={{ width: `${progress}%` }} />
        </div>
      )}
      {hasFile ? (
        <div data-testid={`${testid}-preview`}>
          {previewUrl && kind === "audio" && <audio controls src={previewUrl} className="w-full h-10" data-testid={`${testid}-player`} />}
          {previewUrl && kind === "video" && <video controls src={previewUrl} className="w-full max-h-72" data-testid={`${testid}-player`} />}
          {previewUrl && kind === "image" && <img src={previewUrl} alt="" className="w-full max-h-48 object-cover rounded" data-testid={`${testid}-player`} />}
          {previewUrl && kind === "document" && (
            <a href={previewUrl} target="_blank" rel="noreferrer" data-testid={`${testid}-player`} className="inline-block text-xs uppercase text-brandAccent border border-border px-3 py-2">Open Document</a>
          )}
          {originalFilename && <p className="text-[11px] text-white/40 mt-1.5 truncate">{originalFilename}</p>}
        </div>
      ) : (
        <p className="text-xs text-white/40" data-testid={`${testid}-empty`}>No file uploaded yet.</p>
      )}
    </div>
  );
}

function SeasonPicker({ value, raw, onChange, seasonOptions }) {
  const [customMode, setCustomMode] = useState(false);
  const isKnown = !value || seasonOptions.includes(value);
  return (
    <label className="text-xs uppercase text-white/70 flex items-center gap-2 flex-wrap">
      Season
      {!customMode && isKnown ? (
        <select data-testid="gold-content-form-season" value={value || ""} onChange={(e) => {
          if (e.target.value === "__new__") { setCustomMode(true); return; }
          onChange(e.target.value || null);
        }} className="bg-black border border-white/15 px-2 py-1 text-sm">
          <option value="">No Season</option>
          {seasonOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          <option value="__new__">+ New Season…</option>
        </select>
      ) : (
        <input data-testid="gold-content-form-season-custom" autoFocus value={value || ""} placeholder="e.g. Season 18"
          onChange={(e) => onChange(e.target.value)} onBlur={() => setCustomMode(false)}
          className="bg-black border border-white/15 px-2 py-1 text-sm w-40" />
      )}
      {raw && raw !== value && <span className="text-[11px] text-white/35 normal-case">(source: {raw})</span>}
    </label>
  );
}

function ContentForm({ type, initial, onClose, onSaved, relatedOptions, seasonOptions, suggestedEpisodeNumber }) {
  const api = useApi();
  const [item, setItem] = useState(initial || null);
  const [creatingDraft, setCreatingDraft] = useState(!initial);
  const [saving, setSaving] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const startedRef = useRef(false);
  const syncedRef = useRef(initial || null);
  const itemIdRef = useRef(initial?.id || null);
  const queueRef = useRef(Promise.resolve());

  useEffect(() => {
    if (initial) { syncedRef.current = initial; return; }
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const payload = { type, tier: "gold", title: DEFAULT_TITLE[type] || "Untitled", is_published: false };
        if (type === "episode" && suggestedEpisodeNumber) payload.episode_number = suggestedEpisodeNumber;
        const res = await api.post("/admin/gold/content", payload);
        syncedRef.current = res.data;
        itemIdRef.current = res.data.id;
        setItem(res.data);
      } catch (e) {
        toast.error("Could not start a new draft.");
      } finally {
        setCreatingDraft(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // All saves (blur-autosave and action-button saves) are serialized through
  // this queue, in call order, so a field's blur-save always finishes before
  // a Publish click that follows it — fixes a race where concurrent PATCHes
  // could overwrite each other or publish before a just-typed field saved.
  const patch = (fields, { silent } = {}) => {
    const run = async () => {
      if (!itemIdRef.current) return null;
      setSaving(true);
      try {
        const res = await api.patch(`/admin/gold/content/${itemIdRef.current}`, fields);
        syncedRef.current = res.data;
        setItem(res.data);
        if (!silent) toast.success(fields.is_published ? "Published." : "Saved.");
        onSaved && onSaved();
        return res.data;
      } catch (e) {
        if (!silent) toast.error(e?.response?.data?.detail || "Could not save.");
        throw e;
      } finally {
        setSaving(false);
      }
    };
    const next = queueRef.current.then(run, run);
    queueRef.current = next.catch(() => {});
    return next;
  };

  const set = (field) => (value) => setItem((prev) => ({ ...prev, [field]: value }));
  const blurSave = (field) => () => {
    if (item[field] === syncedRef.current?.[field]) return;
    patch({ [field]: item[field] }, { silent: true }).catch(() => {});
  };

  const uploadMedia = async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/admin/gold/content/${item.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => { if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100)); },
      });
      syncedRef.current = res.data;
      setItem(res.data);
      toast.success("File uploaded.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    }
  };

  const uploadThumbnail = async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/admin/gold/content/${item.id}/thumbnail`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => { if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100)); },
      });
      syncedRef.current = res.data;
      setItem(res.data);
      toast.success("Artwork uploaded.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Artwork upload failed.");
    }
  };

  const acknowledgeReview = async () => {
    setAcknowledging(true);
    try {
      const res = await api.post(`/admin/gold/content/${item.id}/acknowledge-review`);
      syncedRef.current = res.data;
      setItem(res.data);
      toast.success("Marked as reviewed.");
      onSaved && onSaved();
    } catch {
      toast.error("Could not acknowledge.");
    } finally {
      setAcknowledging(false);
    }
  };

  const mediaPreviewUrl = item?.media_storage_path ? adminPreviewUrl(item.id, "file", item.updated_at) : null;
  const thumbPreviewUrl = item?.thumbnail_storage_path ? adminPreviewUrl(item.id, "thumbnail", item.updated_at) : null;

  if (creatingDraft || !item) {
    return <div className="border border-border p-6 mb-4 flex items-center gap-2 text-sm text-foreground/60" data-testid="gold-content-form-creating"><Loader2 className="w-4 h-4 animate-spin" /> Starting a new draft…</div>;
  }

  const hasMedia = type === "signal" || Boolean(item.media_storage_path || item.external_video_url);

  return (
    <div className="border border-border p-4 mb-4 space-y-3" data-testid={`gold-content-form-${type}`}>
      {item.needs_review && (
        <div className="border border-red-400/60 bg-red-950/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="gold-content-needs-review-banner">
          <span className="text-xs text-red-300 flex items-center gap-2"><ShieldAlert className="w-4 h-4 shrink-0" /> {item.needs_review_reason}</span>
          <button data-testid="gold-content-acknowledge-review" onClick={acknowledgeReview} disabled={acknowledging}
            className="inline-flex items-center gap-1.5 text-xs uppercase border border-red-400/60 text-red-300 px-3 py-1.5 hover:border-red-300 disabled:opacity-40 shrink-0">
            <Check className="w-3 h-3" /> {acknowledging ? "Saving…" : "Acknowledge"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <MediaStatusBadge status={item.media_status} />
        {saving && <span className="text-[11px] text-white/40 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
      </div>

      <input data-testid="gold-content-form-title" value={item.title} onChange={(e) => set("title")(e.target.value)} onBlur={blurSave("title")}
        placeholder="Title" className="w-full bg-black border border-white/15 px-3 py-2 text-sm" />
      <textarea data-testid="gold-content-form-description" value={item.description || ""} onChange={(e) => set("description")(e.target.value)} onBlur={blurSave("description")}
        placeholder="Short description" rows={2} className="w-full bg-black border border-white/15 px-3 py-2 text-sm" />
      {(type === "signal") && (
        <textarea data-testid="gold-content-form-body" value={item.body || ""} onChange={(e) => set("body")(e.target.value)} onBlur={blurSave("body")}
          placeholder="Full Signal text" rows={3} className="w-full bg-black border border-white/15 px-3 py-2 text-sm" />
      )}
      {type === "video" && (
        <input data-testid="gold-content-form-video-url" value={item.external_video_url || ""} onChange={(e) => set("external_video_url")(e.target.value)} onBlur={blurSave("external_video_url")}
          placeholder="Embeddable video URL (optional if uploading a file instead)" className="w-full bg-black border border-white/15 px-3 py-2 text-sm" />
      )}

      <div className="flex flex-wrap items-center gap-4">
        {type === "episode" && (
          <SeasonPicker value={item.season_canonical} raw={item.season_raw} seasonOptions={seasonOptions || []}
            onChange={(v) => patch({ season_raw: v || item.season_raw, season_canonical: v }).catch(() => {})} />
        )}
        {type !== "episode" && (
          <input data-testid="gold-content-form-category" value={item.category || ""} onChange={(e) => set("category")(e.target.value)} onBlur={blurSave("category")}
            placeholder="Category" className="bg-black border border-white/15 px-2 py-1 text-sm w-36" />
        )}
        {type === "episode" && (
          <input data-testid="gold-content-form-episode-number" type="number" value={item.episode_number ?? ""} onChange={(e) => set("episode_number")(e.target.value ? Number(e.target.value) : "")} onBlur={blurSave("episode_number")}
            placeholder="Episode #" className="bg-black border border-white/15 px-2 py-1 text-sm w-28" />
        )}
        {(type === "episode" || type === "video") && (
          <input data-testid="gold-content-form-duration" type="number" value={item.duration_seconds ?? ""} onChange={(e) => set("duration_seconds")(e.target.value ? Number(e.target.value) : "")} onBlur={blurSave("duration_seconds")}
            placeholder="Duration (seconds)" className="bg-black border border-white/15 px-2 py-1 text-sm w-40" />
        )}
        <label className="text-xs uppercase text-white/70 flex items-center gap-2">
          Publish Date
          <input data-testid="gold-content-form-publication-date" type="date" value={item.publication_date ? item.publication_date.slice(0, 10) : ""} onChange={(e) => patch({ publication_date: e.target.value }).catch(() => {})} className="bg-black border border-white/15 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs uppercase text-white/70 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="gold-content-form-featured" checked={Boolean(item.is_featured)} onChange={(e) => patch({ is_featured: e.target.checked }).catch(() => {})} />
          <Star className="w-3 h-3" /> Featured
        </label>
      </div>

      {item.season_raw && item.type === "episode" && (
        <p className="text-[11px] text-white/35" data-testid="gold-content-form-season-raw">Historical source season: {item.season_raw} · Historically published {item.historical_published_at ? new Date(item.historical_published_at).toLocaleDateString() : "—"}</p>
      )}

      {type === "companion_asset" && (
        <label className="text-xs uppercase text-white/70 flex items-center gap-2">
          Related Episode / Framework
          <select data-testid="gold-content-form-related" value={item.related_content_id || ""} onChange={(e) => patch({ related_content_id: e.target.value || null }).catch(() => {})} className="bg-black border border-white/15 px-2 py-1 text-sm flex-1">
            <option value="">None</option>
            {relatedOptions?.map((o) => <option key={o.id} value={o.id}>{o.type} — {o.title}</option>)}
          </select>
        </label>
      )}

      <div className="flex items-center gap-4">
        <label className="text-xs uppercase text-white/70 flex items-center gap-2">
          Tier
          <select data-testid="gold-content-form-tier" value={item.tier} onChange={(e) => patch({ tier: e.target.value }).catch(() => {})} className="bg-black border border-white/15 px-2 py-1 text-sm">
            <option value="gold">Gold</option>
            <option value="gold_pro">Gold Pro</option>
          </select>
        </label>
      </div>

      {/* Media upload controls — always visible here in the form itself. */}
      <div className="grid sm:grid-cols-2 gap-3 pt-1">
        {type === "episode" && (
          <MediaUploadField testid="gold-content-upload-audio" label="Audio File" icon={Music} accept="audio/*"
            hasFile={Boolean(item.media_storage_path)} mediaKey={item.media_storage_path} originalFilename={item.original_filename}
            previewUrl={mediaPreviewUrl} kind="audio" uploadFn={uploadMedia} />
        )}
        {type === "video" && (
          <MediaUploadField testid="gold-content-upload-video" label="Video File" icon={Film} accept="video/*"
            hasFile={Boolean(item.media_storage_path)} mediaKey={item.media_storage_path} originalFilename={item.original_filename}
            previewUrl={mediaPreviewUrl} kind="video" uploadFn={uploadMedia} />
        )}
        {(type === "framework" || type === "companion_asset" || type === "signal") && (
          <MediaUploadField testid="gold-content-upload-document" label={type === "signal" ? "Attached Audio/Document (optional)" : "Document File"} icon={FileText} accept={type === "signal" ? "audio/*,application/pdf" : undefined}
            hasFile={Boolean(item.media_storage_path)} mediaKey={item.media_storage_path} originalFilename={item.original_filename}
            previewUrl={mediaPreviewUrl} kind={item.media_content_type?.startsWith("audio/") ? "audio" : "document"} uploadFn={uploadMedia} />
        )}
        <MediaUploadField testid="gold-content-upload-artwork" label="Artwork / Thumbnail" icon={ImageIcon} accept="image/*"
          hasFile={Boolean(item.thumbnail_storage_path)} mediaKey={item.thumbnail_storage_path} originalFilename={item.original_thumbnail_filename}
          previewUrl={thumbPreviewUrl} kind="image" uploadFn={uploadThumbnail} />
      </div>

      <div className="flex gap-2 pt-1 flex-wrap items-center">
        <button data-testid="gold-content-form-save-draft" onClick={() => patch({ is_published: false }).catch(() => {})}
          className="text-xs uppercase text-white/70 border border-border px-3 py-2 disabled:opacity-40">Save Draft</button>
        {!item.is_published && (
          <button data-testid="gold-content-form-publish" onClick={() => patch({ is_published: true }).catch(() => {})} disabled={!hasMedia}
            title={!hasMedia ? "Upload media before publishing" : undefined}
            className="text-xs uppercase text-brandAccent border border-border px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed">Publish</button>
        )}
        {item.is_published && (
          <button data-testid="gold-content-form-unpublish" onClick={() => patch({ is_published: false }).catch(() => {})}
            className="text-xs uppercase text-white/60 border border-border px-3 py-2 disabled:opacity-40">Unpublish</button>
        )}
        <button data-testid="gold-content-form-archive" onClick={() => patch({ archived: !item.archived }).catch(() => {})}
          className="text-xs uppercase text-white/60 border border-border px-3 py-2 disabled:opacity-40 inline-flex items-center gap-1.5">
          <Archive className="w-3 h-3" /> {item.archived ? "Unarchive" : "Archive"}
        </button>
        <button data-testid="gold-content-form-cancel" onClick={onClose} className="text-xs uppercase text-white/50 px-3 py-2 ml-auto">Close</button>
      </div>
      {!hasMedia && <p className="text-[11px] text-white/35" data-testid="gold-content-form-publish-hint">Upload the real file above before this can be published.</p>}
    </div>
  );
}

function ContentRow({ item, onSelect, onDelete, onPreview }) {
  return (
    <div className="border border-border p-4 mb-3 flex flex-wrap items-center gap-4 justify-between" data-testid={`gold-content-row-${item.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{item.title}{item.episode_number != null ? ` · Ep. ${item.episode_number}` : ""}{item.is_featured && <Star className="inline w-3 h-3 ml-1.5 -mt-0.5 text-brandAccent" />}</p>
        <p className="text-xs text-foreground/60 uppercase">{item.tier.replace("_", " ")} · {item.season_canonical || item.category || "—"}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <MediaStatusBadge status={item.media_status} />
        <button data-testid={`gold-content-preview-${item.id}`} onClick={() => onPreview(item)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors">
          <Eye className="w-3 h-3" /> Preview
        </button>
        <button data-testid={`gold-content-edit-${item.id}`} onClick={() => onSelect(item)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors">
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button data-testid={`gold-content-delete-${item.id}`} onClick={() => onDelete(item.id)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function PreviewModal({ item, onClose }) {
  const mediaUrl = item.media_storage_path ? adminPreviewUrl(item.id, "file", item.updated_at) : null;
  const thumbUrl = item.thumbnail_storage_path ? adminPreviewUrl(item.id, "thumbnail", item.updated_at) : null;
  const isVideo = item.media_content_type?.startsWith("video/") || (item.type === "video" && !item.external_video_url);
  const isAudio = item.media_content_type?.startsWith("audio/");
  const isDoc = item.type === "framework" || item.type === "companion_asset";

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-6" onClick={onClose} data-testid="gold-content-preview-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative bg-[#0a0a0a] border border-border p-6 max-w-xl w-full max-h-[80vh] overflow-y-auto">
        <button data-testid="gold-content-preview-close" onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        <p className="text-sm font-medium mb-1">{item.title}</p>
        <p className="text-xs text-foreground/60 uppercase mb-4">{item.tier.replace("_", " ")} · {item.type.replace("_", " ")}</p>
        {thumbUrl && <img src={thumbUrl} alt="" className="w-full rounded mb-4 max-h-56 object-cover" data-testid="gold-content-preview-thumbnail" />}
        {item.external_video_url && (
          <div className="aspect-video bg-black mb-4"><iframe src={item.external_video_url} title={item.title} className="w-full h-full" allowFullScreen /></div>
        )}
        {mediaUrl && isVideo && <video controls src={mediaUrl} className="w-full max-h-96 mb-2" data-testid="gold-content-preview-video" />}
        {mediaUrl && isAudio && <audio controls src={mediaUrl} className="w-full mb-2" data-testid="gold-content-preview-audio" />}
        {mediaUrl && isDoc && (
          <a href={mediaUrl} target="_blank" rel="noreferrer" data-testid="gold-content-preview-document" className="inline-block text-xs uppercase text-brandAccent border border-border px-3 py-2">Open Document</a>
        )}
        {!mediaUrl && !item.external_video_url && <p className="text-white/40 text-sm" data-testid="gold-content-preview-empty">No file uploaded yet.</p>}
      </div>
    </div>
  );
}

const MEDIA_STATUS_FILTERS = ["awaiting_media", "media_uploaded", "ready_for_review", "published", "archived", "needs_attention"];

/** Episodes get a specialized catalog browser: search/filter the 63
 * historical records, pick one to attach media to, or start a brand new
 * episode — kept distinct so admins never confuse the two workflows. */
function EpisodeCatalogManager() {
  const api = useApi();
  const qc = useQueryClient();
  const [mode, setMode] = useState("browse"); // browse | attach | new
  const [selected, setSelected] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [q, setQ] = useState("");
  const [season, setSeason] = useState("");
  const [mediaStatus, setMediaStatus] = useState("");
  const [pubStatus, setPubStatus] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState("newest");

  const { data: seasons } = useQuery({
    queryKey: ["admin-gold-seasons"],
    queryFn: async () => (await api.get("/admin/gold/content/seasons", { params: { type: "episode" } })).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["admin-gold-content", "episode"],
    queryFn: async () => (await api.get("/admin/gold/content", { params: { type: "episode" } })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-gold-content", "episode"] });
    qc.invalidateQueries({ queryKey: ["admin-gold-seasons"] });
  };

  const filtered = useMemo(() => {
    let list = data || [];
    if (q.trim()) {
      const trimmed = q.trim();
      if (/^\d+$/.test(trimmed)) {
        const num = Number(trimmed);
        list = list.filter((i) => i.episode_number === num);
      } else {
        const needle = trimmed.toLowerCase();
        list = list.filter((i) => i.title.toLowerCase().includes(needle));
      }
    }
    if (season) list = list.filter((i) => i.season_canonical === season);
    if (mediaStatus) list = list.filter((i) => i.media_status === mediaStatus);
    if (pubStatus) list = list.filter((i) => (pubStatus === "published" ? i.is_published : !i.is_published));
    if (featuredOnly) list = list.filter((i) => i.is_featured);
    const sorted = [...list].sort((a, b) => (a.episode_number || 0) - (b.episode_number || 0));
    return sort === "oldest" ? sorted : sorted.reverse();
  }, [data, q, season, mediaStatus, pubStatus, featuredOnly, sort]);

  const needsReviewCount = (data || []).filter((i) => i.needs_review).length;
  const suggestedNext = Math.max(0, ...(data || []).map((i) => i.episode_number || 0)) + 1;

  const remove = async (id) => {
    if (!window.confirm("Delete this episode permanently?")) return;
    try {
      await api.delete(`/admin/gold/content/${id}`);
      toast.success("Deleted.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };

  if (mode === "attach" && selected) {
    return (
      <div data-testid="gold-episode-attach-view">
        <button data-testid="gold-episode-back-to-catalog" onClick={() => { setMode("browse"); setSelected(null); refresh(); }} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to Catalog
        </button>
        <ContentForm type="episode" initial={selected} onClose={() => { setMode("browse"); setSelected(null); refresh(); }} onSaved={refresh} seasonOptions={seasons || []} />
      </div>
    );
  }
  if (mode === "new") {
    return (
      <div data-testid="gold-episode-new-view">
        <button data-testid="gold-episode-back-to-catalog" onClick={() => { setMode("browse"); refresh(); }} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to Catalog
        </button>
        <ContentForm type="episode" initial={null} onClose={() => { setMode("browse"); refresh(); }} onSaved={refresh} seasonOptions={seasons || []} suggestedEpisodeNumber={suggestedNext} />
      </div>
    );
  }

  return (
    <div data-testid="gold-episode-catalog">
      <div className="flex flex-wrap gap-2 mb-4">
        <span data-testid="gold-episode-attach-existing-label" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-white/40 px-1 py-2">
          Select an episode below to Attach Media to Existing Episode, or:
        </span>
        <button data-testid="gold-episode-mode-new" onClick={() => setMode("new")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors">
          <Plus className="w-3 h-3" /> Create New Episode
        </button>
        {needsReviewCount > 0 && (
          <span data-testid="gold-episode-needs-review-count" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-red-400/60 text-red-300 px-3 py-2">
            <ShieldAlert className="w-3 h-3" /> {needsReviewCount} Needing Review
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 border border-border p-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="gold-episode-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or episode #"
            className="w-full bg-black border border-white/15 pl-8 pr-2 py-1.5 text-sm" />
        </div>
        <select data-testid="gold-episode-filter-season" value={season} onChange={(e) => setSeason(e.target.value)} className="bg-black border border-white/15 px-2 py-1.5 text-sm">
          <option value="">All Seasons</option>
          {(seasons || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="gold-episode-filter-media-status" value={mediaStatus} onChange={(e) => setMediaStatus(e.target.value)} className="bg-black border border-white/15 px-2 py-1.5 text-sm">
          <option value="">All Media Statuses</option>
          {MEDIA_STATUS_FILTERS.map((s) => <option key={s} value={s}>{MEDIA_STATUS_META[s].label}</option>)}
        </select>
        <select data-testid="gold-episode-filter-pub-status" value={pubStatus} onChange={(e) => setPubStatus(e.target.value)} className="bg-black border border-white/15 px-2 py-1.5 text-sm">
          <option value="">Draft + Published</option>
          <option value="published">Published Only</option>
          <option value="draft">Draft Only</option>
        </select>
        <select data-testid="gold-episode-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-black border border-white/15 px-2 py-1.5 text-sm">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
        <label className="text-xs uppercase text-white/70 flex items-center gap-1.5 cursor-pointer px-1">
          <input type="checkbox" data-testid="gold-episode-filter-featured" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} /> Featured
        </label>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-sm text-foreground/60 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading episode catalog…</div>}
      {!isLoading && filtered.length === 0 && <p className="text-white/50 text-sm py-4" data-testid="gold-episode-empty">No episodes match these filters.</p>}
      {!isLoading && filtered.map((item) => (
        <ContentRow key={item.id} item={item} onSelect={(i) => { setSelected(i); setMode("attach"); }} onDelete={remove} onPreview={setPreviewing} />
      ))}
      {previewing && <PreviewModal item={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function ContentManager({ type }) {
  const api = useApi();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-gold-content", type],
    queryFn: async () => (await api.get("/admin/gold/content", { params: { type } })).data,
  });

  const { data: relatedOptions } = useQuery({
    queryKey: ["admin-gold-content-options"],
    queryFn: async () => (await api.get("/admin/gold/content-options", { params: { types: "episode,framework" } })).data,
    enabled: type === "companion_asset",
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-gold-content", type] });

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await api.delete(`/admin/gold/content/${id}`);
      toast.success("Deleted.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };

  return (
    <div data-testid={`gold-content-manager-${type}`}>
      {!creating && !editing && (
        <button data-testid={`gold-content-new-${type}`} onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-border px-3 py-2 hover:border-foreground transition-colors mb-4">
          <Plus className="w-3 h-3" /> New {CONTENT_TABS.find((t) => t.key === type)?.label.replace(/s$/, "")}
        </button>
      )}
      {creating && <ContentForm type={type} initial={null} onClose={() => { setCreating(false); refresh(); }} onSaved={refresh} relatedOptions={relatedOptions} />}
      {editing && <ContentForm key={editing.id} type={type} initial={editing} onClose={() => { setEditing(null); refresh(); }} onSaved={refresh} relatedOptions={relatedOptions} />}
      {isLoading && <div className="flex items-center gap-2 text-sm text-foreground/60 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {!isLoading && data?.length === 0 && <p className="text-white/50 text-sm py-4" data-testid={`gold-content-empty-${type}`}>Nothing published or drafted yet.</p>}
      {!isLoading && !creating && !editing && data?.map((item) => (
        <ContentRow key={item.id} item={item} onSelect={setEditing} onDelete={remove} onPreview={setPreviewing} />
      ))}
      {previewing && <PreviewModal item={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

function ClaimsManager() {
  const api = useApi();
  const qc = useQueryClient();
  const [resendingId, setResendingId] = useState(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-gold-claims"],
    queryFn: async () => (await api.get("/admin/gold/claims")).data,
    refetchInterval: 15000,
  });

  const resend = async (sessionId) => {
    setResendingId(sessionId);
    try {
      await api.post(`/admin/gold/claims/${sessionId}/resend`);
      toast.success("Activation link resent to the purchaser.");
      qc.invalidateQueries({ queryKey: ["admin-gold-claims"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not resend.");
    } finally {
      setResendingId(null);
    }
  };

  const claims = data?.claims || [];
  const abandoned = data?.abandoned_checkouts || [];

  if (isLoading) return <div className="flex items-center gap-2 text-sm text-foreground/60 py-10" data-testid="gold-claims-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (isError) return <p className="text-sm text-foreground border border-border p-4" data-testid="gold-claims-error">Could not load Gold enrollments.</p>;

  return (
    <div data-testid="gold-claims-manager">
      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-3">Paid Enrollments</h2>
      {claims.length === 0 && <p className="text-white/70 text-sm py-4 mb-4" data-testid="gold-claims-empty">No Gold/Gold Pro purchases yet.</p>}
      {claims.map((c) => <ClaimRow key={c.session_id} claim={c} onResend={resend} resendingId={resendingId} />)}
      {abandoned.length > 0 && (
        <details className="mt-10" data-testid="gold-abandoned-section">
          <summary className="text-sm uppercase tracking-wide text-white/70 mb-3 cursor-pointer">Checkout Initiated, Not Yet Paid ({abandoned.length})</summary>
          <div className="mt-3">{abandoned.map((c) => <ClaimRow key={c.session_id} claim={c} onResend={resend} resendingId={resendingId} />)}</div>
        </details>
      )}
    </div>
  );
}

export default function AdminGold() {
  const [tab, setTab] = useState("episode");

  return (
    <div data-testid="admin-gold-page">
      <h1 className="font-display text-display-xs mb-2">Gold Vault — Content &amp; Enrollments</h1>
      <p className="text-xs text-foreground/60 mb-6">Manage Episodes, Signals, Videos, Frameworks and Companion Assets, and track Gold/Gold Pro purchase-first activations.</p>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
        {[...CONTENT_TABS, { key: "claims", label: "Claims" }].map((t) => (
          <button key={t.key} data-testid={`admin-gold-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`text-xs uppercase tracking-wide px-3 py-2 border ${tab === t.key ? "text-foreground border-foreground" : "text-foreground/50 border-foreground/20"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "claims" ? <ClaimsManager /> : tab === "episode" ? <EpisodeCatalogManager /> : <ContentManager type={tab} />}
    </div>
  );
}
